import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  AlertCircle,
  BookOpen,
  Download,
  FileText,
  Filter,
  Hourglass,
  Loader2,
  Plus,
  Receipt,
  Search,
} from 'lucide-react'
import { api } from '../lib/api'
import { downloadFile } from '../lib/download'
import { formatCents } from '../lib/money'
import {
  InvoiceStatusLabel,
  InvoiceKindLabel,
  type InvoiceListItem,
  type InvoiceListResponse,
  type InvoiceSeriesDto,
  type IssuerDto,
  type InvoiceStatusT,
} from '@toga/shared'

const PAGE_SIZE = 50

export default function Billing() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState<InvoiceListResponse | null>(null)
  const [series, setSeries] = useState<InvoiceSeriesDto[]>([])
  const [issuers, setIssuers] = useState<IssuerDto[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState(searchParams.get('q') ?? '')

  const status = (searchParams.get('status') ?? '') as InvoiceStatusT | ''
  const year = searchParams.get('year') ?? ''
  const seriesId = searchParams.get('seriesId') ?? ''
  const issuerId = searchParams.get('issuerId') ?? ''
  const clientId = searchParams.get('clientId') ?? ''
  const page = Number(searchParams.get('page') ?? '1')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (q.trim()) params.set('q', q.trim())
      if (status) params.set('status', status)
      if (year) params.set('year', year)
      if (seriesId) params.set('seriesId', seriesId)
      if (issuerId) params.set('issuerId', issuerId)
      if (clientId) params.set('clientId', clientId)
      params.set('page', String(page))
      params.set('pageSize', String(PAGE_SIZE))
      const resp = await api.get<InvoiceListResponse>(`/invoices?${params.toString()}`)
      setData(resp)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar facturas')
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [q, status, year, seriesId, issuerId, clientId, page])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    api
      .get<{ items: IssuerDto[] }>('/issuers')
      .then((r) => setIssuers(r.items))
      .catch(() => setIssuers([]))
  }, [])

  useEffect(() => {
    api
      .get<{ items: InvoiceSeriesDto[] }>('/invoice-series')
      .then((r) => setSeries(r.items))
      .catch(() => setSeries([]))
  }, [])

  const updateParam = (k: string, v: string) => {
    const next = new URLSearchParams(searchParams)
    if (v) next.set(k, v)
    else next.delete(k)
    next.delete('page')
    setSearchParams(next, { replace: true })
  }

  const onNewInvoice = async () => {
    const def = series.find((s) => s.isDefault) ?? series[0]
    if (!def) {
      setError('Configura al menos una serie en Ajustes > Facturación')
      return
    }
    // Necesitamos un cliente — abrimos modal sencillo
    navigate('/billing/new')
  }

  const years = useMemo(() => {
    const now = new Date().getFullYear()
    return [now, now - 1, now - 2]
  }, [])

  const [exporting, setExporting] = useState(false)
  const onExport = async () => {
    setExporting(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (status) params.set('status', status)
      if (clientId) params.set('clientId', clientId)
      if (year) {
        params.set('from', `${year}-01-01`)
        params.set('to', `${year}-12-31`)
      }
      const qs = params.toString()
      await downloadFile(`/export/invoices.csv${qs ? `?${qs}` : ''}`, 'facturas.csv')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al exportar')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <Receipt size={20} className="text-toga-300" />
            Facturación
          </h1>
          {data && (
            <p className="text-xs t-muted">
              {data.total} factura{data.total !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/billing/pending"
            title="Cuotas de iguala, hitos y presupuestos que toca facturar"
            className="px-3 py-1.5 rounded-lg text-xs border border-base t-secondary hover:bg-hover hover:text-toga-200 flex items-center gap-1.5"
          >
            <Hourglass size={12} /> Pendientes de facturar
          </Link>
          <Link
            to="/accounting/ledger"
            title="Libro de facturas expedidas, IVA por trimestre, cartera de cobros, gastos y borradores 303/130"
            className="px-3 py-1.5 rounded-lg text-xs border border-base t-secondary hover:bg-hover hover:text-toga-200 flex items-center gap-1.5"
          >
            <BookOpen size={12} /> Contabilidad
          </Link>
          <button
            type="button"
            onClick={() => void onExport()}
            disabled={exporting}
            title="Descargar facturas filtradas en CSV"
            className="px-3 py-1.5 rounded-lg text-xs border border-base t-secondary hover:bg-hover hover:text-toga-200 flex items-center gap-1.5 disabled:opacity-50"
          >
            {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
            Exportar CSV
          </button>
          <button
            type="button"
            onClick={() => void onNewInvoice()}
            className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
          >
            <Plus size={14} /> Nueva factura
          </button>
        </div>
      </header>

      {/* Filtros */}
      <div className="surface p-3 flex items-end gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={14}
            className="absolute left-2 top-1/2 -translate-y-1/2 t-faint pointer-events-none"
          />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Número, cliente o notas…"
            className="w-full bg-transparent border border-base rounded-lg pl-7 pr-3 py-1.5 text-sm t-primary placeholder:t-faint focus:outline-none focus:border-toga-500/50"
          />
        </div>
        <FilterSelect
          label="Estado"
          value={status}
          onChange={(v) => updateParam('status', v)}
          options={[
            { v: '', l: 'Todos' },
            ...(['BORRADOR', 'EMITIDA', 'PARCIAL', 'COBRADA', 'ANULADA'] as const).map((s) => ({
              v: s,
              l: InvoiceStatusLabel[s],
            })),
          ]}
        />
        <FilterSelect
          label="Año"
          value={year}
          onChange={(v) => updateParam('year', v)}
          options={[{ v: '', l: 'Todos' }, ...years.map((y) => ({ v: String(y), l: String(y) }))]}
        />
        <FilterSelect
          label="Emisor"
          value={issuerId}
          onChange={(v) => updateParam('issuerId', v)}
          options={[{ v: '', l: 'Todos' }, ...issuers.map((i) => ({ v: i.id, l: i.name }))]}
        />
        <FilterSelect
          label="Serie"
          value={seriesId}
          onChange={(v) => updateParam('seriesId', v)}
          options={[
            { v: '', l: 'Todas' },
            ...series.map((s) => ({ v: s.id, l: `${s.prefix} · ${s.name}` })),
          ]}
        />
      </div>

      {/* Tabla */}
      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-800/30 text-xs uppercase tracking-wider t-faint">
            <tr>
              <th className="px-3 py-2 text-left">Número</th>
              <th className="px-3 py-2 text-left">Fecha</th>
              <th className="px-3 py-2 text-left">Cliente</th>
              <th className="px-3 py-2 text-left">Tipo</th>
              <th className="px-3 py-2 text-right">Base</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-right">Cobrado</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center t-muted">
                  <Loader2 size={14} className="inline-block animate-spin" /> Cargando…
                </td>
              </tr>
            )}
            {!loading && data && data.items.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-10 text-center">
                  <Receipt size={28} className="mx-auto t-faint mb-2" />
                  <p className="t-muted text-sm">Aún no hay facturas que coincidan.</p>
                </td>
              </tr>
            )}
            {data?.items.map((inv) => (
              <InvoiceRow key={inv.id} inv={inv} />
            ))}
          </tbody>
        </table>
      </div>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}
    </div>
  )
}

function InvoiceRow({ inv }: { inv: InvoiceListItem }) {
  const pending = inv.totalCents - inv.paidCents
  return (
    <tr className="border-t border-base/40 hover:bg-hover">
      <td className="px-3 py-2 font-mono text-toga-300">{inv.number ?? '—'}</td>
      <td className="px-3 py-2 t-secondary">
        {inv.issueDate ? new Date(inv.issueDate).toLocaleDateString('es-ES') : '—'}
      </td>
      <td className="px-3 py-2 t-primary">
        <Link to={`/billing/${inv.id}`} className="hover:underline">
          {inv.clientDisplayName}
        </Link>
      </td>
      <td className="px-3 py-2 text-xs t-faint">{InvoiceKindLabel[inv.kind]}</td>
      <td className="px-3 py-2 text-right t-secondary">{formatCents(inv.subtotalCents)}</td>
      <td className="px-3 py-2 text-right t-primary font-semibold">
        {formatCents(inv.totalCents)}
      </td>
      <td className="px-3 py-2 text-right">
        {inv.paidCents > 0 ? (
          <span className="text-emerald-300">{formatCents(inv.paidCents)}</span>
        ) : (
          <span className="t-faint">—</span>
        )}
        {pending > 0 && inv.status !== 'BORRADOR' && (
          <div className="text-[10px] text-rose-300">{formatCents(pending)} pdte.</div>
        )}
      </td>
      <td className="px-3 py-2">
        <StatusPill status={inv.status} />
      </td>
      <td className="px-3 py-2 text-right">
        <div className="inline-flex items-center gap-1">
          <Link
            to={`/billing/${inv.id}`}
            className="px-2 py-1 rounded text-xs t-secondary hover:bg-hover"
          >
            Abrir
          </Link>
          {inv.status !== 'BORRADOR' && (
            <a
              href={`/api/invoices/${inv.id}/pdf`}
              target="_blank"
              rel="noreferrer"
              className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted hover:text-toga-300"
              title="Ver PDF"
            >
              <FileText size={13} />
            </a>
          )}
        </div>
      </td>
    </tr>
  )
}

function StatusPill({ status }: { status: InvoiceStatusT }) {
  const STYLES: Record<InvoiceStatusT, string> = {
    BORRADOR: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
    EMITIDA: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
    PARCIAL: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    COBRADA: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    ANULADA: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  }
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] border ${STYLES[status]}`}>
      {InvoiceStatusLabel[status]}
    </span>
  )
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: { v: string; l: string }[]
}) {
  return (
    <label className="text-xs t-muted flex items-center gap-2">
      <Filter size={12} className="t-faint" /> {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-toga-500/50"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>
            {o.l}
          </option>
        ))}
      </select>
    </label>
  )
}
